/**
 * URL Filter Parsing Hook
 * 
 * Parses ERPNext-style URL filters (?filters=[["field","operator","value"]])
 * and applies them to the filter store for the specified scope.
 * 
 * This enables shareable/bookmarkable filtered list URLs, matching ERPNext behavior.
 * 
 * URL format: 
 * - ?filters=[...]&doctype=Sales%20Order (short filters)
 * - ?filters_token=...&doctype=Sales%20Order (long filters via token)
 * - ?filters_key=...&doctype=Sales%20Order (long filters via storage fallback)
 * 
 * Converts to FilterDescriptor[] and applies to the scoped filter store.
 */

import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useFilterStore } from "@/lib/store/filterStore";
import type { FilterDescriptor, FilterOperator } from "@/lib/erp";
import type { FrappeFilter } from "@/lib/erp/docs";
import { frappeMethod } from "@/lib/erp/utils";

/**
 * Parse ERPNext-style URL filters and apply to filter store.
 * 
 * @param filterStoreKey - The filter store scope key (e.g., "inventory.item_price")
 * @param expectedDoctype - The expected doctype for validation (optional, for safety)
 * 
 * @example
 * // URL: /inventory/item-price?filters=[["item_code","=","ABC-123"]]&doctype=Item%20Price
 * // This hook will parse and apply the filter to the "inventory.item_price" scope
 */
export function useURLFilters(filterStoreKey: string, expectedDoctype?: string) {
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    // Read doctype from URL (explicit, not heuristic)
    const doctypeFromUrl = searchParams.get("doctype");
    
    // Priority 1: Check for filters_token (backend token endpoint)
    const filtersToken = searchParams.get("filters_token");
    if (filtersToken) {
      if (!doctypeFromUrl) {
        console.warn("[useURLFilters] filters_token present but doctype missing from URL");
        return;
      }
      
      // Fetch filters from token endpoint (token-only retrieval)
      frappeMethod("zaviago_backend.api.relationship_graph.get_list_route_filters_from_token", {
        token: filtersToken,
      })
        .then((response: unknown) => {
          const data = response as { doctype: string; filters: FrappeFilter[] };
          // Validate doctype matches URL (safety check)
          if (data.doctype !== doctypeFromUrl) {
            console.warn(
              `[useURLFilters] Token doctype (${data.doctype}) does not match URL doctype (${doctypeFromUrl})`
            );
            // Still apply filters (token is valid, just doctype mismatch warning)
          }
          
          // Validate against expected doctype if provided
          if (expectedDoctype && data.doctype !== expectedDoctype) {
            console.warn(
              `[useURLFilters] Token doctype (${data.doctype}) does not match expected doctype (${expectedDoctype})`
            );
          }
          
          const filterDescriptors = parseFiltersToDescriptors(data.filters);
          if (filterDescriptors.length > 0) {
            useFilterStore.getState().setFiltersForKey(filterStoreKey, filterDescriptors);
          }
        })
        .catch((e) => {
          console.warn("[useURLFilters] Failed to fetch filters from token:", e);
        });
      return;
    }

    // Priority 2: Check for filters_key (client-side storage fallback)
    const filtersKey = searchParams.get("filters_key");
    if (filtersKey) {
      if (!doctypeFromUrl) {
        console.warn("[useURLFilters] filters_key present but doctype missing from URL");
        return;
      }
      
      try {
        const filtersJson = sessionStorage.getItem(`filters:${filtersKey}`);
        const storedDoctype = sessionStorage.getItem(`filters_doctype:${filtersKey}`);
        const timestampStr = sessionStorage.getItem(`filters_timestamp:${filtersKey}`);
        
        if (!filtersJson || !storedDoctype) {
          console.warn("[useURLFilters] Storage key not found or missing doctype");
          return;
        }
        
        // Check TTL (24 hours)
        if (timestampStr) {
          const timestamp = parseInt(timestampStr, 10);
          const age = Date.now() - timestamp;
          const MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours
          if (age > MAX_AGE_MS) {
            console.warn("[useURLFilters] Storage key expired, cleaning up");
            sessionStorage.removeItem(`filters:${filtersKey}`);
            sessionStorage.removeItem(`filters_doctype:${filtersKey}`);
            sessionStorage.removeItem(`filters_timestamp:${filtersKey}`);
            return;
          }
        }
        
        // Validate stored doctype matches URL doctype
        if (storedDoctype !== doctypeFromUrl) {
          console.warn(
            `[useURLFilters] Stored doctype (${storedDoctype}) does not match URL doctype (${doctypeFromUrl})`
          );
          // Still apply filters (storage is valid, just doctype mismatch warning)
        }
        
        // Validate against expected doctype if provided
        if (expectedDoctype && storedDoctype !== expectedDoctype) {
          console.warn(
            `[useURLFilters] Stored doctype (${storedDoctype}) does not match expected doctype (${expectedDoctype})`
          );
        }
        
        const parsed = JSON.parse(filtersJson);
        if (Array.isArray(parsed)) {
          const filterDescriptors = parseFiltersToDescriptors(parsed);
          if (filterDescriptors.length > 0) {
            useFilterStore.getState().setFiltersForKey(filterStoreKey, filterDescriptors);
          }
          // Clean up storage after use (optional, but good hygiene)
          sessionStorage.removeItem(`filters:${filtersKey}`);
          sessionStorage.removeItem(`filters_doctype:${filtersKey}`);
          sessionStorage.removeItem(`filters_timestamp:${filtersKey}`);
        }
      } catch (e) {
        console.warn("[useURLFilters] Failed to read filters from storage:", e);
      }
      return;
    }

    // Priority 3: Original ?filters= parameter
    const filtersParam = searchParams.get("filters");
    if (!filtersParam) return;

    try {
      // CRITICAL: Decode URI component before parsing (hardening tweak)
      const decoded = decodeURIComponent(filtersParam);
      const parsed = JSON.parse(decoded);
      if (!Array.isArray(parsed)) return;

      // If doctype is in URL, validate it (optional safety check)
      if (doctypeFromUrl && expectedDoctype && doctypeFromUrl !== expectedDoctype) {
        console.warn(
          `[useURLFilters] URL doctype (${doctypeFromUrl}) does not match expected doctype (${expectedDoctype})`
        );
      }

      const filterDescriptors = parseFiltersToDescriptors(parsed);
      if (filterDescriptors.length > 0) {
        useFilterStore.getState().setFiltersForKey(filterStoreKey, filterDescriptors);
      }
    } catch (e) {
      console.warn("[useURLFilters] Failed to parse filters param:", e);
    }
  }, [filterStoreKey, expectedDoctype, searchParams, setSearchParams]);
}

// Helper function to parse filters to descriptors (preserves childDoctype)
function parseFiltersToDescriptors(parsed: any[]): FilterDescriptor[] {
  return parsed
    .map((filter: any[]) => {
      let field: string;
      let op: string;
      let value: any;
      let childDoctype: string | undefined;

      if (filter.length === 4) {
        [childDoctype, field, op, value] = filter;  // Preserve child doctype
      } else if (filter.length === 3) {
        [field, op, value] = filter;
      } else {
        return null;
      }

      const operatorMap: Record<string, FilterOperator> = {
        "=": "equals",
        "!=": "notEquals",
        "like": "like",
        "not like": "notLike",
        ">": "greaterThan",
        "<": "lessThan",
        ">=": "greaterOrEqual",
        "<=": "lessOrEqual",
        "in": "in",
        "not in": "notIn",
        "between": "between",
        "is": "is",
      };

      const operator = operatorMap[op] || "equals";

      return {
        field,
        operator,
        value,
        ...(childDoctype && { childDoctype }),
      } as FilterDescriptor;
    })
    .filter((f): f is FilterDescriptor => f !== null);
}
